import { Form, Formik, type FormikHelpers } from "formik";
import { ImageUp, Trash2, UploadCloud } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { SignaturePad, type SignaturePadHandle } from "@/components/common/signature-pad";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/common/page-header";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { TabShell } from "@/features/settings/components/tab-shell";
import { useAuthedImage } from "@/hooks/use-authed-image";
import {
  createFirmSignature,
  deleteFirmSignature,
  type FirmSignature,
  listFirmSignatures,
  updateFirmSignature,
} from "@/features/settings/firm-api";
import { ApiError } from "@/lib/api";
import { dataUrlToFile } from "@/lib/utils";

function errMessage(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

const ACCEPT = "image/png,image/jpeg";

/** Reject anything that isn't a PNG/JPG so we don't POST an invalid image. */
function isImageFile(file: File) {
  return file.type === "image/png" || file.type === "image/jpeg";
}

/** Drop the extension so a filename can pre-fill the signature name. */
function nameFromFile(file: File) {
  return file.name.replace(/\.[^./\\]+$/, "").slice(0, 80) || "Signature";
}


// --- New signature dialog (name + drag-drop / pick a PNG/JPG) --------------
// A small Formik dialog mirroring letter-template-dialog rather than reusing
// forms/branding-dialogs' SignatureDialog: that one is a whole manager dialog
// (list + resize + delete inline), which doesn't fit this table-driven page's
// single "create" flow.

const newSchema = Yup.object({
  name: Yup.string().trim().required("Name is required").max(80),
});

interface NewValues {
  name: string;
}

interface NewSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected file (e.g. dropped on the empty state) + a suggested name. */
  initialFile?: File | null;
  initialName?: string;
  onCreated: () => void;
}

function NewSignatureDialog({
  open,
  onOpenChange,
  initialFile,
  initialName,
  onCreated,
}: NewSignatureDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<"upload" | "draw">("upload");
  const [hasDrawing, setHasDrawing] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const padRef = useRef<SignaturePadHandle>(null);

  useEffect(() => {
    if (open) {
      setFile(initialFile ?? null);
      setMode("upload");
      setHasDrawing(false);
    }
  }, [open, initialFile]);

  // Create the object URL once per file and revoke it on change/unmount —
  // building it inline in JSX would leak a new URL on every re-render.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function pickFile(next: File | null | undefined) {
    if (!next) return;
    if (!isImageFile(next)) {
      toast.error("Upload a PNG or JPG image.");
      return;
    }
    setFile(next);
  }

  async function handleSubmit(values: NewValues, { setSubmitting }: FormikHelpers<NewValues>) {
    const name = values.name.trim();
    let imageFile = file;
    if (mode === "draw") {
      const pad = padRef.current;
      if (!pad || pad.isEmpty()) {
        toast.error("Draw a signature first.");
        setSubmitting(false);
        return;
      }
      imageFile = dataUrlToFile(pad.toDataURL(), `${name || "signature"}.png`);
    }
    if (!imageFile) {
      toast.error("Add a signature image first.");
      setSubmitting(false);
      return;
    }
    try {
      await createFirmSignature({
        name,
        file: imageFile,
        scale: 100,
        isDefault: false,
      });
      toast.success("Signature added");
      onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't add the signature."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New signature</DialogTitle>
          <DialogDescription>
            A named signature image overlaid on generated letters and forms.
          </DialogDescription>
        </DialogHeader>
        <Formik<NewValues>
          initialValues={{ name: initialName ?? "" }}
          enableReinitialize
          validationSchema={newSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <FormField name="name" label="Name" placeholder="e.g. Managing Partner" />
              <div className="grid gap-2">
                <Label>Signature image</Label>
                <Tabs
                  value={mode}
                  onValueChange={(v) => {
                    setMode(v as "upload" | "draw");
                    // The draw pad remounts (empty) when switching tabs.
                    setHasDrawing(false);
                  }}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="upload">Upload</TabsTrigger>
                    <TabsTrigger value="draw">Draw</TabsTrigger>
                  </TabsList>
                  <TabsContent value="upload" className="mt-3 grid gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept={ACCEPT}
                      hidden
                      onChange={(e) => pickFile(e.target.files?.[0])}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Drop a PNG or JPG, or click to choose a file"
                      onClick={() => fileRef.current?.click()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") fileRef.current?.click();
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        pickFile(e.dataTransfer.files?.[0]);
                      }}
                      className={`flex min-h-32 flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-4 text-center transition-colors ${
                        dragOver ? "border-primary bg-accent" : "border-border"
                      }`}
                    >
                      {file && previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Signature preview"
                          className="max-h-24 object-contain"
                        />
                      ) : (
                        <>
                          <UploadCloud className="text-muted-foreground size-6" />
                          <p className="text-muted-foreground text-sm">
                            Drag &amp; drop a PNG or JPG, or click to choose
                          </p>
                        </>
                      )}
                    </div>
                    {file && <p className="text-muted-foreground text-xs">{file.name}</p>}
                  </TabsContent>
                  <TabsContent value="draw" className="mt-3 grid gap-2">
                    <SignaturePad
                      ref={padRef}
                      onChange={() => setHasDrawing(!(padRef.current?.isEmpty() ?? true))}
                    />
                    <p className="text-muted-foreground text-xs">
                      Draw your signature using a mouse, trackpad or touchscreen.
                    </p>
                  </TabsContent>
                </Tabs>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || (mode === "upload" ? !file : !hasDrawing)}
                >
                  {isSubmitting ? "Adding…" : "Add signature"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

// --- Resize dialog (scale % slider over a live preview) --------------------

interface ResizeDialogProps {
  signature: FirmSignature;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

/** A signature list thumbnail, loaded via the authenticated image endpoint
 * (signatures live in private media — no public URL). The cache-buster keys off
 * `id` (not the image path) since a signature's image is never replaced in
 * place — re-uploading is delete + create, which yields a new id. */
function SignatureThumb({ id, name, hasImage }: { id: string; name: string; hasImage: boolean }) {
  const src = useAuthedImage(hasImage ? `/firm/signatures/${id}/image/?v=${id}` : null);
  return (
    <div className="bg-muted flex h-12 w-24 items-center justify-center overflow-hidden rounded">
      {src ? (
        <img src={src} alt={`${name} signature`} className="max-h-full max-w-full object-contain" />
      ) : (
        <span className="text-muted-foreground text-xs">{hasImage ? "…" : "No image"}</span>
      )}
    </div>
  );
}

function ResizeSignatureDialog({ signature, open, onOpenChange, onSaved }: ResizeDialogProps) {
  const [scale, setScale] = useState(signature.scale);
  const [saving, setSaving] = useState(false);
  const imgSrc = useAuthedImage(
    signature.imageUrl ? `/firm/signatures/${signature.id}/image/?v=${signature.id}` : null,
  );

  useEffect(() => {
    if (open) setScale(signature.scale);
  }, [open, signature.scale]);

  async function handleSave() {
    setSaving(true);
    try {
      await updateFirmSignature(signature.id, { scale });
      toast.success("Signature size saved");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't save the signature size."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resize “{signature.name}”</DialogTitle>
          <DialogDescription>
            Applied when this signature is overlaid on generated letters and forms.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="bg-muted flex min-h-32 items-center justify-center rounded-lg p-4">
            {imgSrc ? (
              <img
                src={imgSrc}
                alt={`${signature.name} preview`}
                style={{ width: `${scale}%` }}
                className="max-h-40 object-contain"
              />
            ) : (
              <p className="text-muted-foreground text-sm">
                {signature.imageUrl ? "Loading…" : "No image."}
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label>Scale — {scale}%</Label>
            <Slider
              min={1}
              max={100}
              step={1}
              value={[scale]}
              onValueChange={(vals) => setScale(vals[0] ?? scale)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Signatures management page --------------------------------------------

export function SignaturesTab() {
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");
  const [signatures, setSignatures] = useState<FirmSignature[] | null>(null);
  const [error, setError] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [droppedName, setDroppedName] = useState("");
  const [resizeTarget, setResizeTarget] = useState<FirmSignature | null>(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    let active = true;
    listFirmSignatures()
      .then((s) => active && setSignatures(s))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  async function refresh() {
    setSignatures(await listFirmSignatures());
  }

  function openNew(file?: File | null) {
    setDroppedFile(file ?? null);
    setDroppedName(file ? nameFromFile(file) : "");
    setNewOpen(true);
  }

  function handleEmptyDrop(file: File | undefined) {
    if (!file) return;
    if (!isImageFile(file)) {
      toast.error("Upload a PNG or JPG image.");
      return;
    }
    openNew(file);
  }

  async function setDefault(sig: FirmSignature) {
    try {
      await updateFirmSignature(sig.id, { isDefault: true });
      toast.success("Default signature set");
      await refresh();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't set the default."));
    }
  }

  async function handleDelete(sig: FirmSignature) {
    try {
      await deleteFirmSignature(sig.id);
      setSignatures((prev) => prev?.filter((s) => s.id !== sig.id) ?? null);
      toast.success("Signature deleted");
    } catch (err) {
      toast.error(errMessage(err, "Couldn't delete the signature."));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Signatures"
        subtitle="Signature images inserted into generated letters and forms. The default is applied automatically."
        actions={
          isAdmin && signatures ? (
            <Button type="button" size="sm" className="gap-1.5" onClick={() => openNew()}>
              <ImageUp className="size-4" />
              New signature
            </Button>
          ) : undefined
        }
      />
      <TabShell loading={!signatures && !error} error={error}>
        {signatures && (
        <>
          {signatures.length === 0 ? (
            <div
              role={isAdmin ? "button" : undefined}
              tabIndex={isAdmin ? 0 : undefined}
              aria-label={isAdmin ? "Drop a PNG or JPG to add a signature" : undefined}
              onClick={isAdmin ? () => openNew() : undefined}
              onKeyDown={
                isAdmin
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") openNew();
                    }
                  : undefined
              }
              onDragOver={
                isAdmin
                  ? (e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }
                  : undefined
              }
              onDragLeave={isAdmin ? () => setDragOver(false) : undefined}
              onDrop={
                isAdmin
                  ? (e) => {
                      e.preventDefault();
                      setDragOver(false);
                      handleEmptyDrop(e.dataTransfer.files?.[0]);
                    }
                  : undefined
              }
              className={`flex min-h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-8 text-center transition-colors ${
                dragOver ? "border-primary bg-accent" : "border-border"
              }`}
            >
              <UploadCloud className="text-muted-foreground size-8" />
              <p className="text-muted-foreground text-sm">
                {isAdmin
                  ? "No signatures yet. Drag & drop a PNG or JPG, or click to add one."
                  : "No signatures yet."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Signature</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Scale</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signatures.map((sig) => (
                    <TableRow key={sig.id}>
                      <TableCell>
                        <SignatureThumb id={sig.id} name={sig.name} hasImage={!!sig.imageUrl} />
                      </TableCell>
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">
                          {sig.name}
                          {sig.isDefault && <Badge variant="secondary">Default</Badge>}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{sig.scale}%</TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => setResizeTarget(sig)}
                            >
                              Resize
                            </Button>
                            {/*
                              No "Clear image": FirmSignature.image is non-nullable
                              on the backend, so nulling the image while keeping the
                              row isn't supported yet. This disabled control stands
                              in for the legacy Clear-image; delete + re-upload covers
                              the need until a nullable-image backend change lands.
                            */}
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              disabled
                              title="Re-upload to replace the image"
                            >
                              Clear image
                            </Button>
                            {!sig.isDefault && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setDefault(sig)}
                              >
                                Set default
                              </Button>
                            )}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive gap-1.5"
                                >
                                  <Trash2 className="size-4" />
                                  Delete
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete this signature?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    &quot;{sig.name}&quot; will be removed and no longer available
                                    to insert into letters or forms. This can&apos;t be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(sig)}
                                    className="bg-destructive text-white hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {isAdmin && (
            <NewSignatureDialog
              open={newOpen}
              onOpenChange={setNewOpen}
              initialFile={droppedFile}
              initialName={droppedName}
              onCreated={refresh}
            />
          )}
          {isAdmin && resizeTarget && (
            <ResizeSignatureDialog
              signature={resizeTarget}
              open={!!resizeTarget}
              onOpenChange={(open) => !open && setResizeTarget(null)}
              onSaved={refresh}
            />
          )}
        </>
      )}
      </TabShell>
    </div>
  );
}
